import { REPORT_TEMPLATE, type IReportCardInfo, type CoreInnovationCardData } from '@/types';
import type { BaseCardService } from './abstract-base-card-service';
import { BaseCardServiceImpl, type BaseReportCardData } from './abstract-base-card-service';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { ReportApiService } from '@/infrastructure/api-functions/report-api-service';
import { CoreInnovationApiService } from '@/infrastructure/api-functions/core-innovation-api-service';
import { I18nService } from '../i18n-service';
import { Message } from '@patsnap/tahiti-core/common/message';
import _ from 'lodash'

export const CoreInnovationService = Symbol('CoreInnovationService');

export interface CoreInnovationService extends BaseCardService<CoreInnovationCardData> {
    setProjectId(projectId: string): void;
    addPatent(patentId: string): Promise<void>;
    removePatent(patentId: string): Promise<void>;
}

const MAX_PATENT_COUNT = 10;

@injectable()
export class CoreInnovationServiceImpl extends BaseCardServiceImpl<CoreInnovationCardData> implements CoreInnovationService {
    _modelData = {
        isLoading: false,
        isFailed: false,
        isSuccess: false,
        data: undefined,
    };

    private _projectId = '';

    templateType = REPORT_TEMPLATE.KEY_PATENT_INTERPRETATION;

    constructor(
    @inject(ReportApiService) _reportApiService: ReportApiService,
    @inject(CoreInnovationApiService) private _coreInnovationApiService: CoreInnovationApiService,
    @inject(I18nService) private _i18nService: I18nService,
    @inject(Message) private _message: Message
    ) {
        super(
            _reportApiService
        );
    }

    setProjectId(projectId: string) {
        this._projectId = projectId;
    }

    get projectId() {
        return this._projectId;
    }

    private _generateSolutionList(cardData: BaseReportCardData<IReportCardInfo>) {
        const data = cardData.data?.[REPORT_TEMPLATE.KEY_PATENT_INTERPRETATION]?.KEY_PATENT_INTERPRETATION_GPT
        return {
            solutionList: data,
            isAddBusy: false,
        }
    }

    updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void {
        const updatedValue = getUpdateValue(_.omit(this.modelData, 'data'));

        const dataHaveFromUpdated = _.has(updatedValue, 'data')

        if (dataHaveFromUpdated) {
            this.update(modelData => ({
                ...modelData,
                ...updatedValue,
                data: this._generateSolutionList(updatedValue),
            }));
        } else {
            this.update(m => ({
                ...m,
                ...updatedValue,
                data: m.data,
            }));
        }
    }

    async addPatent(patentId: string) {
        const solutionList = this.modelData.data?.solutionList
        const position = _.findIndex(solutionList, (patent) => patent.SOLUTION_ID === patentId) + 1;
        if (position) {
            this._message.warning({
                message: this._i18nService.t('detail.patentInterpret.alreadyInList', { index: position }),
            })
            return;
        }
        if (solutionList && solutionList.length >= MAX_PATENT_COUNT) {
            this._message.warning({
                message: this._i18nService.t('detail.patentInterpret.addReportLimit', { count: MAX_PATENT_COUNT }),
            })
            return;
        }
        this.update(v => ({
            ...v,
            data: {
                ...v.data,
                isAddBusy: true,
            },
        }))
        try {
            const patent = await this._coreInnovationApiService.addPatent(this._projectId, patentId);
            this.update(v => ({
                ...v,
                data: {
                    ...v.data,
                    solutionList: [patent, ...(v.data?.solutionList || [])],
                },
            }))
        } finally {
            this.update(v => ({
                ...v,
                data: {
                    ...v.data,
                    isAddBusy: false,
                },
            }))
        }
    }

    async removePatent(patentId: string) {
        const solutionList = this.modelData.data?.solutionList
        if (!solutionList) return
        await this._coreInnovationApiService.removePatent(this._projectId, patentId);
        this.update(v => ({
            ...v,
            data: {
                ...v.data,
                solutionList: solutionList.filter(patent => patent.SOLUTION_ID !== patentId),
            },
        }))
    }
}
